export const smbOnboarding2 = {
  title: "Create Your Business Page",
  subtitle: "Complete the steps below so AI can build your page",
  navigation: {
    back: "Back",
    next: "Next",
    finish: "Finish",
    step: "Step",
    of: "/",
  },
  toast: {
    name_required: "Name Required",
    name_required_desc: "Please enter your business name before continuing.",
    saved: "Successfully Saved!",
    saved_desc: "Your onboarding data has been submitted. AI will build your page shortly.",
    error: "Error",
    error_desc: "There was an error saving. Please try again.",
  },
  steps: {
    business_info: {
      title: "Business Basics",
      description: "Enter your business basic information.",
      business_name: "Business Name",
      business_name_placeholder: "e.g. Beautiful Hair Salon",
      business_category: "Category",
      business_category_placeholder: "Select your category",
      bio: "Short Description",
      bio_placeholder: "Tell us about your business, what you do, what makes you special...",
      logo: "Logo",
      upload_logo: "Upload Logo",
      logo_help: "Click to upload (min. 500x500px recommended)",
      address: "Address",
      address_placeholder: "1234 Example Street",
      phone: "Phone Number",
      phone_placeholder: "+1 234 567 8900",
      email: "Email Address",
      email_placeholder: "info@mybusiness.com",
      categories: {
        salon: "Hair Salon",
        barbershop: "Barbershop",
        beauty_salon: "Beauty Salon",
        nail_salon: "Nail Salon",
        spa: "Spa / Wellness",
        massage: "Massage",
        yoga_studio: "Yoga Studio",
        fitness_center: "Fitness Center",
        pilates_studio: "Pilates Studio",
        dance_studio: "Dance Studio",
        personal_training: "Personal Training",
        nutritionist: "Nutritionist",
        physiotherapy: "Physiotherapy",
        dental: "Dental Office",
        veterinary: "Veterinary Clinic",
        cafe: "Cafe",
        restaurant: "Restaurant",
        bakery: "Bakery",
        photography: "Photography Studio",
        tutoring: "Tutoring",
        music_school: "Music School",
        art_studio: "Art Studio",
        consulting: "Consulting",
        accounting: "Accounting",
        legal: "Legal Service",
        real_estate: "Real Estate",
        cleaning: "Cleaning",
        repair: "Repair / Service",
        pet_grooming: "Pet Grooming",
        other: "Other",
      },
      tip_title: "Tip",
      tip_description: "Basic information helps your customers find you. Make sure all data is accurate and up-to-date.",
    },
    services: {
      title: "Services",
      description: "Enter the services you offer to your customers.",
      add_service: "Add Service",
      service_name: "Service Name",
      service_name_placeholder: "e.g. Women's Haircut",
      service_description: "Description",
      service_description_placeholder: "Short description of the service",
      service_price: "Price ($)",
      service_price_placeholder: "50",
      service_duration: "Duration (minutes)",
      service_duration_placeholder: "60",
      no_services: "No services added yet",
      services_added: "services added",
      remove_service: "Delete",
      tip_title: "Tip",
      tip_description: "Provide clear prices and durations. This helps your customers decide and book appointments.",
    },
    opening_hours: {
      title: "Opening Hours",
      description: "Enter your business opening hours.",
      day_closed: "Closed",
      day_open: "Open",
      from: "From",
      to: "To",
      days: {
        monday: "Monday",
        tuesday: "Tuesday",
        wednesday: "Wednesday",
        thursday: "Thursday",
        friday: "Friday",
        saturday: "Saturday",
        sunday: "Sunday",
      },
      tip_title: "Tip",
      tip_description: "Accurate opening hours help your customers plan.",
    },
    team: {
      title: "Team (optional)",
      description: "Add your team members who work at your business.",
      add_member: "Add Team Member",
      member_name: "Name",
      member_name_placeholder: "e.g. Jane Doe",
      member_role: "Position",
      member_role_placeholder: "e.g. Hairstylist",
      member_photo: "Photo",
      upload_photo: "Upload Photo",
      no_members: "No team members added yet",
      members_added: "team members added",
      remove_member: "Delete",
      tip_title: "Tip",
      tip_description: "Introducing your team makes your business more personal. This step is optional.",
    },
    target_audience: {
      title: "Target Audience",
      description: "Define who your services are for.",
      age_groups: "Age Groups",
      age_group_options: {
        teens: "Teenagers (13-17)",
        young_adults: "Young Adults (18-25)",
        adults: "Adults (26-40)",
        middle_aged: "Middle-aged (41-60)",
        seniors: "Seniors (60+)",
      },
      communication_style: "Communication Style",
      communication_style_placeholder: "e.g. Friendly, direct, expert",
      communication_styles: {
        friendly: "Friendly",
        professional: "Professional",
        casual: "Casual",
        expert: "Expert",
        motivating: "Motivating",
      },
      keywords: "Keywords",
      keywords_placeholder: "e.g. quality, reliability, personalized",
      add_keyword: "Add Keyword",
      tip_title: "Tip",
      tip_description: "Knowing your target audience helps AI generate personalized content and communication for your page.",
    },
    social_media: {
      title: "Social Media",
      description: "Add your social media profile links.",
      instagram: "Instagram",
      instagram_placeholder: "https://instagram.com/username",
      facebook: "Facebook",
      facebook_placeholder: "https://facebook.com/pagename",
      tiktok: "TikTok",
      tiktok_placeholder: "https://tiktok.com/@username",
      youtube: "YouTube",
      youtube_placeholder: "https://youtube.com/@channelname",
      linkedin: "LinkedIn",
      linkedin_placeholder: "https://linkedin.com/in/username",
      x: "X (Twitter)",
      x_placeholder: "https://x.com/username",
      tip_title: "Tip",
      tip_description: "Social media links help your page visitors find you on other platforms too.",
    },
    digital_products: {
      title: "Digital Products",
      description: "What digital products would you like to sell on your page?",
      select_types: "Select types",
      types: {
        ebook: "E-book",
        video_course: "Video Course",
        template: "Template",
        audio: "Audio / Podcast",
        worksheet: "Worksheet",
        recipe: "Recipe Collection",
        guide: "Guide",
        checklist: "Checklist",
        meal_plan: "Meal Plan",
        training_plan: "Training Plan",
        printable: "Printable Material",
        other: "Other",
      },
      description_label: "Notes about digital products",
      description_placeholder: "Briefly describe what digital products you plan to create...",
      no_selection: "No type selected yet",
      tip_title: "Tip",
      tip_description: "Digital products provide passive income. AI will design your page based on the types you select here.",
    },
    design: {
      title: "Design Preferences",
      description: "Choose your preferred visual style for your page.",
      style_label: "Design Style",
      styles: {
        modern: "Modern & Minimalist",
        elegant: "Elegant & Luxury",
        playful: "Playful & Colorful",
        professional: "Professional & Business",
        natural: "Natural & Organic",
        bold: "Bold & Eye-catching",
      },
      color_label: "Primary Color",
      colors: {
        blue: "Blue",
        purple: "Purple",
        pink: "Pink",
        red: "Red",
        orange: "Orange",
        yellow: "Yellow",
        green: "Green",
        teal: "Teal",
        gray: "Gray",
        black: "Black",
      },
      font_label: "Font Style",
      fonts: {
        modern: "Modern (Manrope)",
        classic: "Classic (Merriweather)",
        elegant: "Elegant (Playfair Display)",
        clean: "Clean (Inter)",
        creative: "Creative (Comfortaa)",
      },
      theme_label: "Theme Mode",
      themes: {
        light: "Light",
        dark: "Dark",
        auto: "Automatic",
      },
      references_label: "Inspiration / Reference",
      references_placeholder: "Any website or style you like? Describe it or add links...",
      tip_title: "Pro Tip",
      tip_description: "Good design builds trust. Choose a style that reflects your business personality.",
    },
  },
};
